import React, { useContext } from "react";
import clsx from "clsx";
import { Row, Col } from "react-bootstrap";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import { ChangeHandler } from "react-hook-form";
import { UserRoles } from "./UserRolesEnum";
import { ValidationMessage } from "../shared/ValidationMessage";
import { AuthContext } from "../contexts/AuthContext";

interface UserRolesDropdownProps {
  onSelectUserRole: (userRole: UserRoles) => void;
  selectedUserRole?: UserRoles | undefined;
  invalid?: boolean;
  invalidMessage?: string;
  showLabel?: boolean;
  onChange?: ChangeHandler;
}

const UserRolesDropdown = React.forwardRef<Dropdown, UserRolesDropdownProps & DropdownProps>((props, ref) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  const { onSelectUserRole, selectedUserRole, invalid, invalidMessage, showLabel, onChange, ...rest } = props;

  const userRoles = Object.values(UserRoles)
    .filter((userRole) => !(userRole === UserRoles.SuperAdmin && !authContext.canI("add", "superAdmin")))
    .map((userRole) => ({
      name: t(`userManagement.${userRole}`),
      type: userRole,
    }));

  function handleChange(e: any) {
    onSelectUserRole(e.value.type as UserRoles);
  }

  const getUserRoleFromEnumValue = (userRole: UserRoles) => {
    return userRoles.find((userRoleOption) => userRoleOption.type === userRole);
  };

  return (
    <Row className="d-flex flex-column">
      {showLabel && (
        <Col xs={"auto"} className="d-flex align-items-center">
          <Typography variant="subtitle2" color="textPrimary">
            {t("userManagement.userRole")}:{" "}
          </Typography>
        </Col>
      )}
      <Col xs={"auto"}>
        <Dropdown
          ref={ref}
          className={clsx({ "p-invalid": invalid })}
          value={selectedUserRole && getUserRoleFromEnumValue(selectedUserRole)}
          options={userRoles}
          onChange={handleChange}
          optionLabel="name"
          filter
          filterBy="name"
          placeholder={t("userManagement.selectUserRole")}
          {...rest}
        />
        {invalid && invalidMessage && (
          <ValidationMessage invalidMessage={invalidMessage} validationType="error" showIcon />
        )}
      </Col>
    </Row>
  );
});

export { UserRolesDropdown };
